import React, {useEffect, useMemo, useState} from "react";
import {useLazyQuery} from "@apollo/client";
import {Box, Chip, TableCell, Tooltip, Typography} from "@mui/material";
import {grey} from "@mui/material/colors";

import {ApolloFetchPolicies} from "../../constants/FetchPolicies";
import {buildTagCountsBySentiment, buildTagViewModel} from "../../helpers/SessionHelper";
import {TAG_COLORS} from "../../helpers/MemberTagHelper";
import {FETCH_CHILD_TAG_METRICS} from "../../queries/GraphQLQueries";

import CurrentTagsDisplay from "../common/CurrentTagsDisplay";

const styles = {
    tagsBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '1.5rem',
        width: '4rem',
        gap: '0.2rem',
        borderRadius: '0.75rem',
        boxShadow: '0 0.125rem 0.5rem rgba(0, 0, 0, 0.08), 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.06)',
    },
    tagChip: {
        height: '1rem',
        fontSize: '0.7rem',
        backgroundColor: TAG_COLORS.POSITIVE,
        color: 'white',
    },
    tooltip: {
        bgcolor: grey[200],
        color: 'black',
        '& .MuiTooltip-arrow': {
            color: grey[200],
        },
    },
    tooltipContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '0.25rem',
        maxWidth: '18.75rem',
    },
    tooltipTitle: {
        fontWeight: 600,
        fontSize: '0.875rem',
    },
};

export default function ChildTagCell({childId, userTriggeredManualRefresh}) {
    const [currentTags, setCurrentTags] = useState([]);
    const [fetchTagsData, {data, loading: isChildTagMetricsLoading}] = useLazyQuery(FETCH_CHILD_TAG_METRICS, {fetchPolicy: ApolloFetchPolicies.NETWORK_ONLY});

    useEffect(() => {
        if (childId) {
            fetchTagsData({
                variables: {childId}
            });
        }
    }, [childId, fetchTagsData, userTriggeredManualRefresh]);

    const tagMetrics = useMemo(() => {
        if (!data?.fetchChildTagMetrics) {
            return null;
        }

        return data.fetchChildTagMetrics.filter(tagMetric => tagMetric.tag.isInternal);
    }, [data?.fetchChildTagMetrics]);

    const tagCountPerSentiment = useMemo(() => {
        return buildTagCountsBySentiment(tagMetrics);
    }, [tagMetrics]);

    useEffect(() => {
        if (tagMetrics?.length > 0) {
            const internalTagViewModels = tagMetrics.map(tagMetric => buildTagViewModel(tagMetric.tag, tagMetric.count));
            setCurrentTags(internalTagViewModels);
        } else {
            setCurrentTags([]);
        }
    }, [tagMetrics]);

    const tooltipContent = (
        <Box sx={styles.tooltipContainer}>
            <Typography sx={styles.tooltipTitle}>Child Tags</Typography>
            <CurrentTagsDisplay currentTags={currentTags} isLoading={isChildTagMetricsLoading}/>
        </Box>
    );

    return (
        <TableCell align="left">
            <Tooltip title={tooltipContent} arrow slotProps={{tooltip: {sx: styles.tooltip}}}>
                <Box sx={styles.tagsBox}>
                  <Chip label={tagCountPerSentiment.positiveCount} size="small" sx={styles.tagChip}/>
                  <Chip label={tagCountPerSentiment.negativeCount} size="small" sx={{...styles.tagChip, backgroundColor: TAG_COLORS.NEGATIVE}}/>
                </Box>
            </Tooltip>
        </TableCell>
    );
}